import React, { useState, useMemo, useEffect } from "react";
import { TextField, Autocomplete } from "@mui/material";
import debounce from "lodash.debounce";

const AsyncAutocomplete = ({
  value,                
  onChange,             
  label,
  fetchFn,              
  fetchByIdFn,          
  getOptionLabel,       
  disabled = false,     
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const fetchOptions = async (search) => {
    if (search.length < 2) return;
    try {
      const response = await fetchFn(1, 10, search.trim().split(' ').shift());
      const fetched = response?.data || [];
      setOptions((prev) => {
        const selected = prev.find((o) => o.id === value);
        if (selected && !fetched.some((o) => o.id === selected.id)) {
          return [...fetched, selected];
        }
        return fetched;
      });
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const debouncedFetch = useMemo(
    () => debounce((query) => fetchOptions(query), 300),
    [value]
  );

  useEffect(() => {
    const loadSelected = async () => {
      if (value && fetchByIdFn) {
        try {
          const response = await fetchByIdFn(value);
          const selected = response?.data || response;
          if (selected) {
            setOptions((prev) => {
              if (!prev.some((o) => o.id === selected.id)) {
                return [...prev, selected];
              }
              return prev;
            });
            onChange?.(selected ? selected.id : "", selected ? selected : null);
          }
        } catch (error) {
          console.error("Error fetching selected option:", error);
        }
      }
    };
    loadSelected();
  }, [value, fetchByIdFn]);

  return (
    <Autocomplete
      {...props}
      disabled={disabled} // <-- disable Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={options.find((o) => o.id === value) || null}
      onChange={(event, newValue) =>
        onChange?.(newValue ? newValue.id : "", newValue ? newValue : null)
      }
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        debouncedFetch(newInputValue);
      }}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          disabled={disabled} 
        />
      )}
      noOptionsText={
        inputValue.length === 0
          ? ""
          : `Can't find "${inputValue}"`
      }
    />
  );
};

export default AsyncAutocomplete;